import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  CircularProgress,
  Avatar,
  alpha,
  useTheme,
  Select,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputAdornment,
} from '@mui/material';
import { MoreVert, Visibility, Block, CheckCircle, Verified, Cancel, Search, AccountBalanceWallet, PlayCircle, Edit, Save, Close, Add, Remove, Delete, Email, Phone, CalendarToday, School, LocationOn, Book, Work, Business, Description, Language, Star, PictureAsPdf, VideoFile, OpenInNew } from '@mui/icons-material';
import { apiService } from '../services/api.service';
import { authService } from '../services/auth.service';
import { firebaseService } from '../services/firebase.service';
import { User, PaginationResult } from '../types';
import Layout from '../components/Layout';
import { getProfile, getAvatarUrl } from '../utils/user';

const Users: React.FC = () => {
  const theme = useTheme();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsTab, setDetailsTab] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedProfile, setEditedProfile] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [walletEditOpen, setWalletEditOpen] = useState(false);
  const [walletAmount, setWalletAmount] = useState('');
  const [walletOperation, setWalletOperation] = useState<'add' | 'subtract' | 'set'>('add');
  const [walletDescription, setWalletDescription] = useState('');
  const [updatingWallet, setUpdatingWallet] = useState(false);

  // For tutor profile editing
  const [languages, setLanguages] = useState<string[]>([]);
  const [languageInput, setLanguageInput] = useState('');
  const [skills, setSkills] = useState<string[]>([]);
  const [skillInput, setSkillInput] = useState('');
  const [selectedWeeklyAvailability, setSelectedWeeklyAvailability] = useState<string[]>([]);
  const [selectedTimingSlots, setSelectedTimingSlots] = useState<string[]>([]);
  const [selectedSessionDuration, setSelectedSessionDuration] = useState<string>('');

  // Experience options matching app
  const experienceOptions = [
    'Less than 1 year',
    '1-2 years',
    '3-5 years',
    '6-10 years',
    '11-15 years',
    '16-20 years',
    'More than 20 years',
  ];

  // Session duration options matching app
  const sessionDurationOptions = [
    '30 minutes',
    '45 minutes',
    '1 hour',
    '1.5 hours',
    '2 hours',
  ];

  // Weekly availability days matching app
  const weeklyAvailabilityDays = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  // Generate timing slots based on session duration (matching app logic)
  const generateTimingSlots = (duration: string): string[] => {
    if (!duration) return [];

    let durationMinutes = 30;
    switch (duration) {
      case '30 minutes':
        durationMinutes = 30;
        break;
      case '45 minutes':
        durationMinutes = 45;
        break;
      case '1 hour':
        durationMinutes = 60;
        break;
      case '1.5 hours':
        durationMinutes = 90;
        break;
      case '2 hours':
        durationMinutes = 120;
        break;
    }

    const slots: string[] = [];
    const startHour = 6;
    const endHour = 22;

    for (let hour = startHour; hour < endHour; hour++) {
      let minute = 0;
      while (minute < 60) {
        const totalStartMinutes = hour * 60 + minute;
        const totalEndMinutes = totalStartMinutes + durationMinutes;

        const endHourCalc = Math.floor(totalEndMinutes / 60);
        const endMinute = totalEndMinutes % 60;

        if (endHourCalc > endHour || (endHourCalc === endHour && endMinute > 0)) {
          break;
        }

        const startPeriod = hour < 12 ? 'AM' : 'PM';
        const displayStartHour = hour === 0 ? 12 : (hour > 12 ? hour - 12 : hour);
        const displayStartTime = `${displayStartHour}:${minute.toString().padStart(2, '0')} ${startPeriod}`;

        const endPeriod = endHourCalc < 12 ? 'AM' : 'PM';
        const displayEndHour = endHourCalc === 0 ? 12 : (endHourCalc > 12 ? endHourCalc - 12 : endHourCalc);
        const displayEndTime = `${displayEndHour}:${endMinute.toString().padStart(2, '0')} ${endPeriod}`;

        slots.push(`${displayStartTime} - ${displayEndTime}`);

        minute += durationMinutes;
      }
    }

    return slots;
  };

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const filters: any = {
        page: page + 1,
        limit: rowsPerPage,
        search,
      };

      let result: PaginationResult<User>;
      if (tab === 1) {
        result = await apiService.getStudents(filters);
      } else if (tab === 2) {
        result = await apiService.getTutors(filters);
      } else {
        result = await apiService.getUsers(filters);
      }

      setUsers(result.data);
      setTotal(result.pagination.total);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, tab]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewDetails = () => {
    setDetailsOpen(true);
    setDetailsTab(0);
    setIsEditMode(false);
    setEditedProfile(null);
    handleMenuClose();
  };

  const handleEditProfile = () => {
    if (selectedUser) {
      const profile = getProfile(selectedUser);
      if (profile) {
        // Convert dob to date string if it exists
        const profileCopy = { ...profile };
        if (profileCopy.dob) {
          const dobDate = firebaseService.convertTimestamp(profileCopy.dob);
          profileCopy.dob = dobDate.toISOString().split('T')[0];
        }
        setEditedProfile(profileCopy);

        // Set languages and skills for tutors
        if (selectedUser.role === 'tutor') {
          setLanguages(Array.isArray(profileCopy.languages) ? [...profileCopy.languages] : []);
          setSkills(Array.isArray(profileCopy.skills) ? [...profileCopy.skills] : []);
          setSelectedWeeklyAvailability(Array.isArray(profileCopy.weeklyAvailability) ? [...profileCopy.weeklyAvailability] : []);
          setSelectedTimingSlots(Array.isArray(profileCopy.timingSlots) ? [...profileCopy.timingSlots] : []);
          setSelectedSessionDuration(profileCopy.sessionDuration || '');
        }
      } else {
        setEditedProfile({});
        if (selectedUser.role === 'tutor') {
          setLanguages([]);
          setSkills([]);
          setSelectedWeeklyAvailability([]);
          setSelectedTimingSlots([]);
          setSelectedSessionDuration('');
        }
      }
      // Store original phone for comparison
      (selectedUser as any).originalPhone = selectedUser.phone;
      setIsEditMode(true);
    }
  };

  const handleAddLanguage = () => {
    if (languageInput.trim() && !languages.includes(languageInput.trim())) {
      setLanguages([...languages, languageInput.trim()]);
      setLanguageInput('');
    }
  };

  const handleRemoveLanguage = (lang: string) => {
    setLanguages(languages.filter(l => l !== lang));
  };

  const handleAddSkill = () => {
    if (skillInput.trim() && !skills.includes(skillInput.trim())) {
      setSkills([...skills, skillInput.trim()]);
      setSkillInput('');
    }
  };

  const handleRemoveSkill = (skill: string) => {
    setSkills(skills.filter(s => s !== skill));
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedProfile(null);
    setLanguages([]);
    setSkills([]);
    setLanguageInput('');
    setSkillInput('');
    setSelectedWeeklyAvailability([]);
    setSelectedTimingSlots([]);
    setSelectedSessionDuration('');
  };

  const handleSaveProfile = async () => {
    if (!selectedUser || !editedProfile) return;

    setSaving(true);
    try {
      // Prepare profile data
      const profileData: any = { ...editedProfile };

      // Add languages and skills for tutors
      if (selectedUser.role === 'tutor') {
        profileData.languages = languages;
        profileData.skills = skills;
        profileData.weeklyAvailability = selectedWeeklyAvailability;
        profileData.timingSlots = selectedTimingSlots;
        profileData.sessionDuration = selectedSessionDuration;
      }

      // Parse subjects/courses from comma-separated string
      if (profileData.subjects && typeof profileData.subjects === 'string') {
        profileData.subjects = profileData.subjects.split(',').map((s: string) => s.trim()).filter((s: string) => s);
      }

      // Parse subjectsOfInterest for students
      if (profileData.subjectsOfInterest && typeof profileData.subjectsOfInterest === 'string') {
        profileData.subjectsOfInterest = profileData.subjectsOfInterest.split(',').map((s: string) => s.trim()).filter((s: string) => s);
      }

      // Update profile
      await apiService.updateUserProfile(selectedUser.id, selectedUser.role, profileData);

      // Update phone number if changed (for students)
      if (selectedUser.phone !== (selectedUser as any).originalPhone) {
        await apiService.updateUser(selectedUser.id, { phone: selectedUser.phone });
      }

      await loadUsers();
      // Refresh selected user
      const updatedUser = await apiService.getUser(selectedUser.id);
      setSelectedUser(updatedUser);
      setIsEditMode(false);
      setEditedProfile(null);
      setLanguages([]);
      setSkills([]);
      setLanguageInput('');
      setSkillInput('');
      setSelectedWeeklyAvailability([]);
      setSelectedTimingSlots([]);
      setSelectedSessionDuration('');
    } catch (error) {
      console.error('Failed to update profile:', error);
      alert('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleProfileFieldChange = (field: string, value: any) => {
    setEditedProfile((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleOpenWalletEdit = () => {
    setWalletAmount('');
    setWalletOperation('add');
    setWalletDescription('');
    setWalletEditOpen(true);
  };

  const handleCloseWalletEdit = () => {
    setWalletEditOpen(false);
    setWalletAmount('');
    setWalletOperation('add');
    setWalletDescription('');
  };

  const handleUpdateWallet = async () => {
    if (!selectedUser || !walletAmount || isNaN(parseFloat(walletAmount)) || parseFloat(walletAmount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    setUpdatingWallet(true);
    try {
      const amount = parseFloat(walletAmount);
      const currentBalance = selectedUser.wallet?.balance || 0;
      let newBalance: number;

      if (walletOperation === 'add') {
        newBalance = currentBalance + amount;
      } else if (walletOperation === 'subtract') {
        newBalance = Math.max(0, currentBalance - amount);
      } else {
        newBalance = amount;
      }

      await apiService.updateWalletBalance(
        selectedUser.id,
        newBalance,
        walletOperation,
        amount,
        walletDescription || `Admin ${walletOperation === 'add' ? 'added' : walletOperation === 'subtract' ? 'deducted' : 'set'} ₹${amount}`
      );

      await loadUsers();
      // Refresh selected user
      const updatedUser = await apiService.getUser(selectedUser.id);
      setSelectedUser(updatedUser);
      handleCloseWalletEdit();
    } catch (error) {
      console.error('Failed to update wallet:', error);
      alert('Failed to update wallet. Please try again.');
    } finally {
      setUpdatingWallet(false);
    }
  };

  const handleSuspend = async () => {
    if (selectedUser) {
      try {
        await apiService.suspendUser(selectedUser.id);
        loadUsers();
      } catch (error) {
        console.error('Failed to suspend user:', error);
      }
    }
    handleMenuClose();
  };

  const handleActivate = async () => {
    if (selectedUser) {
      try {
        await apiService.activateUser(selectedUser.id);
        loadUsers();
      } catch (error) {
        console.error('Failed to activate user:', error);
      }
    }
    handleMenuClose();
  };

  const handleVerifyTutor = async () => {
    if (selectedUser && selectedUser.role === 'tutor') {
      try {
        await apiService.verifyTutor(selectedUser.id);
        loadUsers();
      } catch (error) {
        console.error('Failed to verify tutor:', error);
      }
    }
    handleMenuClose();
  };

  const handlePauseWallet = async (id: string, paused: boolean) => {
    try {
      await apiService.pauseTutorWallet(id, paused);
      loadUsers();
    } catch (error) {
      console.error('Failed to toggle wallet status:', error);
    }
  };

  return (
    <Layout>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <Box sx={{
          mb: 4,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2
        }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
              User Directory
            </Typography>
            <Typography color="text.secondary" variant="body2">
              Manage and monitor all users across the platform.
            </Typography>
          </Box>
        </Box>

        <Paper sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: theme.shadows[2] }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, bgcolor: theme.palette.mode === 'light' ? 'grey.50' : alpha(theme.palette.common.white, 0.02) }}>
            <Tabs
              value={tab}
              onChange={(e, newValue) => setTab(newValue)}
              sx={{
                '& .MuiTab-root': { py: 2, fontWeight: 600, fontSize: '0.875rem' },
                '& .Mui-selected': { color: 'primary.main' }
              }}
            >
              <Tab label="All Users" />
              <Tab label="Students" />
              <Tab label="Tutors" />
            </Tabs>
          </Box>

          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                placeholder="Search users..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                variant="outlined"
                InputProps={{
                  startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: theme.palette.mode === 'light' ? 'grey.50' : alpha(theme.palette.common.white, 0.05),
                    '& fieldset': { borderColor: alpha(theme.palette.divider, 0.1) },
                    '&:hover fieldset': { borderColor: 'primary.light' },
                    '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                  }
                }}
              />
            </Box>

            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table sx={{ minWidth: 800 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: theme.palette.mode === 'light' ? 'grey.50' : alpha(theme.palette.common.white, 0.04) }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>User</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>Contact</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>Role</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>Verification</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>Joined On</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.map((user) => (
                        <TableRow
                          key={user.id}
                          hover
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar
                                src={getAvatarUrl(user)}
                                sx={{
                                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                                  color: 'primary.main',
                                  fontWeight: 700,
                                  fontSize: '0.875rem',
                                  width: 40,
                                  height: 40,
                                  border: '2px solid',
                                  borderColor: alpha(theme.palette.primary.main, 0.2)
                                }}
                              >
                                {((user.studentProfile?.name || user.tutorProfile?.name || 'U').charAt(0)).toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                  {user.studentProfile?.name || user.tutorProfile?.name || 'N/A'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                  {user.id.substring(0, 8)}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{user.email}</Typography>
                            <Typography variant="caption" color="text.secondary">{user.phone}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={user.role}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.625rem',
                                textTransform: 'uppercase',
                                bgcolor: user.role === 'tutor' ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.grey[500], 0.15),
                                color: user.role === 'tutor' ? 'primary.main' : 'text.secondary',
                                borderRadius: 1.5,
                                border: '1px solid',
                                borderColor: user.role === 'tutor' ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.grey[500], 0.2)
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={user.isActive !== false ? 'Active' : 'Suspended'}
                              size="small"
                              sx={{
                                fontWeight: 800,
                                fontSize: '0.625rem',
                                textTransform: 'uppercase',
                                bgcolor: user.isActive !== false ? alpha(theme.palette.success.main, 0.15) : alpha(theme.palette.error.main, 0.15),
                                color: `${user.isActive !== false ? 'success' : 'error'}.main`,
                                borderRadius: 1.5,
                                border: '1px solid',
                                borderColor: user.isActive !== false ? alpha(theme.palette.success.main, 0.2) : alpha(theme.palette.error.main, 0.2),
                                height: 24
                              }}
                            />
                            {user.role === 'tutor' && user.wallet?.isWalletPaused && (
                              <Chip
                                icon={<AccountBalanceWallet sx={{ fontSize: '1rem !important' }} />}
                                label="Wallet Paused"
                                size="small"
                                color="warning"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, borderRadius: 1, mt: 0.5, display: 'flex' }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {user.role === 'tutor' ? (
                              <Chip
                                icon={user.tutorProfile?.isVerified ? <Verified sx={{ fontSize: '1rem !important' }} /> : <Cancel sx={{ fontSize: '1rem !important' }} />}
                                label={user.tutorProfile?.isVerified ? 'Verified' : 'Unverified'}
                                variant="outlined"
                                color={user.tutorProfile?.isVerified ? 'success' : 'warning'}
                                size="small"
                                sx={{ fontWeight: 700, fontSize: '0.625rem', borderRadius: 1 }}
                              />
                            ) : (
                              <Typography variant="caption" color="text.disabled">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              {firebaseService.convertTimestamp(user.createdAt).toLocaleDateString(undefined, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton onClick={(e) => handleMenuClick(e, user)} size="small" sx={{ color: 'text.secondary' }}>
                              <MoreVert fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={total}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  sx={{
                    borderTop: 1,
                    borderColor: 'divider',
                    '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: 'text.secondary'
                    }
                  }}
                />
              </>
            )}
          </Box>
        </Paper>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          elevation={4}
          PaperProps={{
            sx: {
              minWidth: 180,
              borderRadius: 3,
              mt: 1,
              boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)'
            }
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={handleViewDetails} sx={{ gap: 1.5, py: 1.25 }}>
            <Visibility fontSize="small" color="action" />
            <Typography variant="body2" fontWeight={600}>View Profile</Typography>
          </MenuItem>
          {authService.getCurrentUser()?.role !== 'support' && (
            <>
              {selectedUser?.isActive !== false ? (
                <MenuItem onClick={handleSuspend} sx={{ gap: 1.5, py: 1.25, color: 'error.main' }}>
                  <Block fontSize="small" />
                  <Typography variant="body2" fontWeight={600}>Suspend Account</Typography>
                </MenuItem>
              ) : (
                <MenuItem onClick={handleActivate} sx={{ gap: 1.5, py: 1.25, color: 'success.main' }}>
                  <CheckCircle fontSize="small" />
                  <Typography variant="body2" fontWeight={600}>Activate Account</Typography>
                </MenuItem>
              )}
              {selectedUser?.role === 'tutor' && (
                <MenuItem onClick={handleVerifyTutor} sx={{ gap: 1.5, py: 1.25, color: 'primary.main' }}>
                  <Verified fontSize="small" />
                  <Typography variant="body2" fontWeight={600}>Verify Credentials</Typography>
                </MenuItem>
              )}
              {selectedUser?.role === 'tutor' && (
                <MenuItem onClick={() => { handlePauseWallet(selectedUser.id, !selectedUser.wallet?.isWalletPaused); handleMenuClose(); }} sx={{ gap: 1.5, py: 1.5 }}>
                  {selectedUser.wallet?.isWalletPaused ? <PlayCircle fontSize="small" color="success" /> : <AccountBalanceWallet fontSize="small" color="warning" />}
                  <Typography variant="body2" fontWeight={600}>
                    {selectedUser.wallet?.isWalletPaused ? 'Resume Wallet' : 'Pause Wallet'}
                  </Typography>
                </MenuItem>
              )}
            </>
          )}
        </Menu>

        <Dialog
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              boxShadow: theme.shadows[10],
              backgroundImage: 'none',
              m: { xs: 2, sm: 'auto' },
              overflow: 'hidden'
            }
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.common.white, 0.02) }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 4, pt: 3 }}>
              <DialogTitle sx={{ fontWeight: 800, pb: 1, pt: 0, px: 0 }}>User Insight</DialogTitle>
              {detailsTab === 1 && !isEditMode && getProfile(selectedUser) && (
                <Button
                  startIcon={<Edit />}
                  onClick={handleEditProfile}
                  variant="outlined"
                  size="small"
                  sx={{ borderRadius: 2, fontWeight: 700 }}
                >
                  Edit Profile
                </Button>
              )}
            </Box>
            <Tabs value={detailsTab} onChange={(e, v) => { setDetailsTab(v); setIsEditMode(false); setEditedProfile(null); }} sx={{ px: 4, '& .MuiTab-root': { fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1, py: 2 } }}>
              <Tab label="Account" />
              <Tab label="Profile" />
              <Tab label="Raw Data" />
            </Tabs>
          </Box>

          <DialogContent sx={{ p: 0 }}>
            {selectedUser && (
              <Box>
                {detailsTab === 0 && (
                  <Box sx={{ p: 4, display: 'grid', gap: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Avatar
                        src={getAvatarUrl(selectedUser)}
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: 'primary.main',
                          fontWeight: 800,
                          fontSize: '1.5rem',
                          width: 80,
                          height: 80,
                          border: '4px solid',
                          borderColor: 'background.paper',
                          boxShadow: theme.shadows[2]
                        }}
                      >
                        {(selectedUser.studentProfile?.name || selectedUser.tutorProfile?.name || 'U').charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                          {selectedUser.studentProfile?.name || selectedUser.tutorProfile?.name || 'N/A'}
                        </Typography>
                        <Chip label={selectedUser.role} size="small" color="primary" sx={{ fontWeight: 800, textTransform: 'uppercase', fontSize: '0.625rem', height: 20, borderRadius: 1 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 500 }}>
                          Access granted on {firebaseService.convertTimestamp(selectedUser.createdAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 4 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 0.5 }}>Account ID</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace', bgcolor: alpha(theme.palette.divider, 0.1), p: 1, borderRadius: 1 }}>{selectedUser.id}</Typography>
                      </Box>
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block' }}>Wallet Balance</Typography>
                          {authService.getCurrentUser()?.role === 'super_admin' && (
                            <Button
                              size="small"
                              startIcon={<Edit />}
                              onClick={handleOpenWalletEdit}
                              variant="outlined"
                              sx={{ minWidth: 'auto', px: 1.5, py: 0.5, fontSize: '0.7rem', borderRadius: 2 }}
                            >
                              Edit
                            </Button>
                          )}
                        </Box>
                        <Typography variant="h5" color="primary.main" sx={{ fontWeight: 900 }}>₹{selectedUser.wallet?.balance.toLocaleString() || '0'}</Typography>
                        {selectedUser.wallet?.isWalletPaused && (
                          <Chip
                            icon={<AccountBalanceWallet sx={{ fontSize: '1rem !important' }} />}
                            label="Wallet Paused"
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ mt: 1, fontSize: '0.65rem', fontWeight: 700 }}
                          />
                        )}
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 0.5 }}>Direct Contact</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedUser.email}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>{selectedUser.phone || 'N/A'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 0.5 }}>Operational Status</Typography>
                        <Chip
                          label={selectedUser.isActive !== false ? 'Active Account' : 'Suspended'}
                          color={selectedUser.isActive !== false ? 'success' : 'error'}
                          sx={{ fontWeight: 800, textTransform: 'uppercase', fontSize: '0.625rem', height: 24, borderRadius: 1.5 }}
                        />
                      </Box>
                    </Box>
                  </Box>
                )}

                {detailsTab === 1 && (
                  <Box sx={{ p: 4 }}>
                    {getProfile(selectedUser) ? (
                      <Box sx={{ display: 'grid', gap: 4 }}>
                        {isEditMode ? (
                          // Edit Mode
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
                            {/* Name */}
                            <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 1 }}>Name</Typography>
                              <TextField
                                fullWidth
                                value={editedProfile?.name || ''}
                                onChange={(e) => handleProfileFieldChange('name', e.target.value)}
                                variant="outlined"
                                size="small"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                              />
                            </Box>

                            {/* Date of Birth */}
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 1 }}>Date of Birth</Typography>
                              <TextField
                                fullWidth
                                type="date"
                                value={editedProfile?.dob || ''}
                                onChange={(e) => handleProfileFieldChange('dob', e.target.value)}
                                variant="outlined"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                              />
                            </Box>

                            {/* Gender */}
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 1 }}>Gender</Typography>
                              <TextField
                                fullWidth
                                select
                                SelectProps={{ native: true }}
                                value={editedProfile?.gender || ''}
                                onChange={(e) => handleProfileFieldChange('gender', e.target.value)}
                                variant="outlined"
                                size="small"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                              >
                                <option value="">Unspecified</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="other">Other</option>
                                <option value="prefer not to say">Prefer not to say</option>
                              </TextField>
                            </Box>

                            {selectedUser.role === 'student' ? (
                              <>
                                {/* Email Address */}
                                <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 1 }}>Email Address</Typography>
                                  <TextField
                                    fullWidth
                                    value={selectedUser.email || ''}
                                    variant="outlined"
                                    size="small"
                                    InputProps={{
                                      startAdornment: <InputAdornment position="start"><Email fontSize="small" /></InputAdornment>,
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, bgcolor: alpha(theme.palette.divider, 0.05) }}
                                    disabled
                                  />
                                </Box>

                                {/* Mobile Number */}
                                <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 1 }}>Mobile Number</Typography>
                                  <TextField
                                    fullWidth
                                    value={selectedUser.phone || ''}
                                    onChange={(e) => {
                                      // Update phone in user object, not profile
                                      setSelectedUser({ ...selectedUser, phone: e.target.value });
                                    }}
                                    variant="outlined"
                                    size="small"
                                    placeholder="Enter mobile number"
                                    InputProps={{
                                      startAdornment: <InputAdornment position="start"><Phone fontSize="small" /></InputAdornment>,
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                  />
                                </Box>

                                {/* Education */}
                                <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 1 }}>Education</Typography>
                                  <TextField
                                    fullWidth
                                    value={editedProfile?.education || ''}
                                    onChange={(e) => handleProfileFieldChange('education', e.target.value)}
                                    variant="outlined"
                                    size="small"
                                    placeholder="Enter education level"
                                    InputProps={{
                                      startAdornment: <InputAdornment position="start"><School fontSize="small" /></InputAdornment>,
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                  />
                                </Box>

                                {/* Location */}
                                <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 1 }}>Location</Typography>
                                  <TextField
                                    fullWidth
                                    value={editedProfile?.location || editedProfile?.address || ''}
                                    onChange={(e) => {
                                      handleProfileFieldChange('location', e.target.value);
                                      handleProfileFieldChange('address', e.target.value);
                                    }}
                                    variant="outlined"
                                    size="small"
                                    placeholder="Enter location"
                                    InputProps={{
                                      startAdornment: <InputAdornment position="start"><LocationOn fontSize="small" /></InputAdornment>,
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                  />
                                </Box>

                                {/* Course Interest */}
                                <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 1 }}>Course Interest</Typography>
                                  <TextField
                                    fullWidth
                                    value={Array.isArray(editedProfile?.subjectsOfInterest) ? editedProfile.subjectsOfInterest.join(', ') : ''}
                                    onChange={(e) => handleProfileFieldChange('subjectsOfInterest', e.target.value)}
                                    variant="outlined"
                                    size="small"
                                    placeholder="Enter courses of interest (comma-separated)"
                                    InputProps={{
                                      startAdornment: <InputAdornment position="start"><Book fontSize="small" /></InputAdornment>,
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                  />
                                </Box>

                                {/* Gender - Radio Buttons */}
                                <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 1 }}>Gender</Typography>
                                  <FormControl component="fieldset">
                                    <RadioGroup
                                      row
                                      value={editedProfile?.gender || 'Male'}
                                      onChange={(e) => handleProfileFieldChange('gender', e.target.value)}
                                    >
                                      <FormControlLabel value="Male" control={<Radio />} label="Male" />
                                      <FormControlLabel value="Female" control={<Radio />} label="Female" />
                                    </RadioGroup>
                                  </FormControl>
                                </Box>
                              </>
                            ) : (
                              <>
                                {/* Email Address */}
                                <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 1 }}>Email Address</Typography>
                                  <TextField
                                    fullWidth
                                    value={selectedUser.email || ''}
                                    variant="outlined"
                                    size="small"
                                    InputProps={{
                                      startAdornment: <InputAdornment position="start"><Email fontSize="small" /></InputAdornment>,
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, bgcolor: alpha(theme.palette.divider, 0.05) }}
                                    disabled
                                  />
                                </Box>

                                {/* Current Company / Institution */}
                                <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 1 }}>Current Company / Institution</Typography>
                                  <TextField
                                    fullWidth
                                    value={editedProfile?.company || ''}
                                    onChange={(e) => handleProfileFieldChange('company', e.target.value)}
                                    variant="outlined"
                                    size="small"
                                    placeholder="e.g., MIT - Massachusetts Institute of Technology"
                                    InputProps={{
                                      startAdornment: <InputAdornment position="start"><Business fontSize="small" /></InputAdornment>,
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                  />
                                </Box>

                                {/* Your Position / Role */}
                                <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 1 }}>Your Position / Role</Typography>
                                  <TextField
                                    fullWidth
                                    value={editedProfile?.position || ''}
                                    onChange={(e) => handleProfileFieldChange('position', e.target.value)}
                                    variant="outlined"
                                    size="small"
                                    placeholder="e.g., Senior Professor"
                                    InputProps={{
                                      startAdornment: <InputAdornment position="start"><Work fontSize="small" /></InputAdornment>,
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                  />
                                </Box>

                                {/* Company Description */}
                                <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 1 }}>Company Description</Typography>
                                  <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    value={editedProfile?.companyDescription || ''}
                                    onChange={(e) => handleProfileFieldChange('companyDescription', e.target.value)}
                                    variant="outlined"
                                    size="small"
                                    placeholder="Describe your company / role"
                                    InputProps={{
                                      startAdornment: <InputAdornment position="start"><Description fontSize="small" /></InputAdornment>,
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                  />
                                </Box>

                                {/* Years of Experience - Dropdown */}
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 1 }}>Years of Experience</Typography>
                                  <FormControl fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                                    <Select
                                      value={editedProfile?.experience || ''}
                                      onChange={(e) => handleProfileFieldChange('experience', e.target.value)}
                                      displayEmpty
                                      startAdornment={<InputAdornment position="start"><Work fontSize="small" /></InputAdornment>}
                                    >
                                      <MenuItem value="">Select years of experience</MenuItem>
                                      {experienceOptions.map((opt) => (
                                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Box>

                                {/* Hourly Rate */}
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 1 }}>Hourly Rate / Cost</Typography>
                                  <TextField
                                    fullWidth
                                    type="number"
                                    value={editedProfile?.hourlyRate || editedProfile?.cost || ''}
                                    onChange={(e) => handleProfileFieldChange('hourlyRate', parseFloat(e.target.value) || 0)}
                                    variant="outlined"
                                    size="small"
                                    placeholder="Enter hourly rate"
                                    InputProps={{
                                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                  />
                                </Box>

                                {/* Courses Offered (Subjects) */}
                                <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 1 }}>Courses Offered</Typography>
                                  <TextField
                                    fullWidth
                                    value={Array.isArray(editedProfile?.subjects) ? editedProfile.subjects.join(', ') : ''}
                                    onChange={(e) => handleProfileFieldChange('subjects', e.target.value)}
                                    variant="outlined"
                                    size="small"
                                    placeholder="Subjects you teach (comma-separated)"
                                    InputProps={{
                                      startAdornment: <InputAdornment position="start"><Book fontSize="small" /></InputAdornment>,
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                  />
                                </Box>

                                {/* Languages */}
                                <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 1 }}>Languages Comfortable With</Typography>
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                    {languages.map((lang) => (
                                      <Chip
                                        key={lang}
                                        label={lang}
                                        onDelete={() => handleRemoveLanguage(lang)}
                                        deleteIcon={<Delete fontSize="small" />}
                                        size="small"
                                        sx={{ borderRadius: 1 }}
                                      />
                                    ))}
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField
                                      fullWidth
                                      value={languageInput}
                                      onChange={(e) => setLanguageInput(e.target.value)}
                                      onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddLanguage(); } }}
                                      variant="outlined"
                                      size="small"
                                      placeholder="Add a language (e.g., English)"
                                      InputProps={{
                                        startAdornment: <InputAdornment position="start"><Language fontSize="small" /></InputAdornment>,
                                      }}
                                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                    <Button
                                      variant="contained"
                                      onClick={handleAddLanguage}
                                      sx={{ minWidth: 48, borderRadius: 2 }}
                                    >
                                      <Add />
                                    </Button>
                                  </Box>
                                </Box>

                                {/* Skills */}
                                <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 1 }}>Skills</Typography>
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                    {skills.map((skill) => (
                                      <Chip
                                        key={skill}
                                        label={skill}
                                        onDelete={() => handleRemoveSkill(skill)}
                                        deleteIcon={<Delete fontSize="small" />}
                                        size="small"
                                        sx={{ borderRadius: 1 }}
                                      />
                                    ))}
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField
                                      fullWidth
                                      value={skillInput}
                                      onChange={(e) => setSkillInput(e.target.value)}
                                      onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddSkill(); } }}
                                      variant="outlined"
                                      size="small"
                                      placeholder="Add a skill (e.g., Mathematics, Physics)"
                                      InputProps={{
                                        startAdornment: <InputAdornment position="start"><Star fontSize="small" /></InputAdornment>,
                                      }}
                                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                    <Button
                                      variant="contained"
                                      onClick={handleAddSkill}
                                      sx={{ minWidth: 48, borderRadius: 2 }}
                                    >
                                      <Add />
                                    </Button>
                                  </Box>
                                </Box>

                                {/* About Me (Bio) */}
                                <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 1 }}>About Me</Typography>
                                  <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    value={editedProfile?.bio || ''}
                                    onChange={(e) => handleProfileFieldChange('bio', e.target.value)}
                                    variant="outlined"
                                    size="small"
                                    placeholder="Write about yourself"
                                    InputProps={{
                                      startAdornment: <InputAdornment position="start"><Description fontSize="small" /></InputAdornment>,
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                  />
                                </Box>

                                {/* Session Duration */}
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 1 }}>Session Duration</Typography>
                                  <FormControl fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                                    <Select
                                      value={selectedSessionDuration}
                                      onChange={(e) => {
                                        setSelectedSessionDuration(e.target.value);
                                        setSelectedTimingSlots([]); // Clear timing slots when duration changes
                                      }}
                                      displayEmpty
                                      startAdornment={<InputAdornment position="start"><CalendarToday fontSize="small" /></InputAdornment>}
                                    >
                                      <MenuItem value="">Select duration</MenuItem>
                                      {sessionDurationOptions.map((opt) => (
                                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Box>

                                {/* Weekly Availability - FilterChips */}
                                <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 1 }}>Weekly Availability</Typography>
                                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                      {weeklyAvailabilityDays.map((day) => {
                                        const isSelected = selectedWeeklyAvailability.includes(day);
                                        return (
                                          <Chip
                                            key={day}
                                            label={day}
                                            onClick={() => {
                                              if (isSelected) {
                                                setSelectedWeeklyAvailability(selectedWeeklyAvailability.filter(d => d !== day));
                                              } else {
                                                setSelectedWeeklyAvailability([...selectedWeeklyAvailability, day]);
                                              }
                                            }}
                                            color={isSelected ? 'primary' : 'default'}
                                            variant={isSelected ? 'filled' : 'outlined'}
                                            sx={{ fontWeight: isSelected ? 700 : 500 }}
                                          />
                                        );
                                      })}
                                    </Box>
                                  </Paper>
                                </Box>

                                {/* Timing Slots - FilterChips (only shown when session duration is selected) */}
                                {selectedSessionDuration && (
                                  <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 1 }}>Timing Slots</Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: '0.7rem' }}>
                                      Select available time slots based on your session duration
                                    </Typography>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, maxHeight: 300, overflow: 'auto' }}>
                                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {generateTimingSlots(selectedSessionDuration).map((slot) => {
                                          const isSelected = selectedTimingSlots.includes(slot);
                                          return (
                                            <Chip
                                              key={slot}
                                              label={slot}
                                              onClick={() => {
                                                if (isSelected) {
                                                  setSelectedTimingSlots(selectedTimingSlots.filter(s => s !== slot));
                                                } else {
                                                  setSelectedTimingSlots([...selectedTimingSlots, slot]);
                                                }
                                              }}
                                              color={isSelected ? 'primary' : 'default'}
                                              variant={isSelected ? 'filled' : 'outlined'}
                                              size="small"
                                              sx={{ fontWeight: isSelected ? 700 : 500, fontSize: '0.75rem' }}
                                            />
                                          );
                                        })}
                                      </Box>
                                    </Paper>
                                  </Box>
                                )}
                              </>
                            )}

                            {/* Address */}
                            <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 1 }}>Residential Address</Typography>
                              <TextField
                                fullWidth
                                multiline
                                rows={2}
                                value={editedProfile?.address || ''}
                                onChange={(e) => handleProfileFieldChange('address', e.target.value)}
                                variant="outlined"
                                size="small"
                                placeholder="Enter full address"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                              />
                            </Box>
                          </Box>
                        ) : (
                          // View Mode
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 4 }}>
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 0.5 }}>Birthday / Age</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {getProfile(selectedUser)?.dob ? (
                                  (() => {
                                    const dob = getProfile(selectedUser)?.dob;
                                    if (!dob) return 'Not Provided';
                                    const date = new Date(dob);
                                    if (isNaN(date.getTime())) return 'Invalid Date';
                                    return (
                                      <>
                                        {date.toLocaleDateString()}
                                        <Chip label={`${Math.floor((new Date().getTime() - date.getTime()) / (1000 * 60 * 60 * 24 * 365.25))} Years`} size="small" variant="outlined" sx={{ ml: 1, height: 20, fontSize: '0.6rem', fontWeight: 800 }} />
                                      </>
                                    );
                                  })()
                                ) : 'Not Provided'}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 0.5 }}>Gender Identifier</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>{getProfile(selectedUser)?.gender || 'Unspecified'}</Typography>
                            </Box>

                            {selectedUser.role === 'student' ? (
                              <>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 0.5 }}>Educational Level</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{getProfile(selectedUser)?.grade || 'Not Specified'}</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 0.5 }}>Institution</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{getProfile(selectedUser)?.school || 'Not Specified'}</Typography>
                                </Box>
                              </>
                            ) : (
                              <>
                                <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 0.5 }}>Professional Summary</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 500, fontStyle: 'italic', bgcolor: alpha(theme.palette.info.main, 0.05), p: 2, borderRadius: 2, borderLeft: '4px solid', borderColor: 'info.main' }}>
                                    "{getProfile(selectedUser)?.bio || 'This tutor has not written a biography yet.'}"
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 0.5 }}>Academic Credentials</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{Array.isArray(getProfile(selectedUser)?.qualifications) ? getProfile(selectedUser)?.qualifications.join(', ') : getProfile(selectedUser)?.qualifications || 'Pending Upload'}</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 0.5 }}>Experience Level</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{getProfile(selectedUser)?.experience || 'Not Specified'}</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 0.5 }}>Session Duration</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{getProfile(selectedUser)?.sessionDuration || 'Not Set'}</Typography>
                                </Box>
                                <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 0.5 }}>Subject Specialties</Typography>
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                                    {getProfile(selectedUser)?.subjects?.map((s: string) => (
                                      <Chip key={s} label={s} size="small" sx={{ fontWeight: 700, borderRadius: 1 }} />
                                    )) || <Typography variant="body2" color="text.disabled">None listed</Typography>}
                                  </Box>
                                </Box>

                                {/* Languages */}
                                {getProfile(selectedUser)?.languages?.length > 0 && (
                                  <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 0.5 }}>Languages</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                                      {getProfile(selectedUser)?.languages.map((lang: string) => (
                                        <Chip key={lang} label={lang} size="small" sx={{ fontWeight: 700, borderRadius: 1 }} />
                                      ))}
                                    </Box>
                                  </Box>
                                )}

                                {/* Skills */}
                                {getProfile(selectedUser)?.skills?.length > 0 && (
                                  <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 0.5 }}>Skills</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                                      {getProfile(selectedUser)?.skills.map((skill: string) => (
                                        <Chip key={skill} label={skill} size="small" sx={{ fontWeight: 700, borderRadius: 1 }} />
                                      ))}
                                    </Box>
                                  </Box>
                                )}

                                {/* Resume */}
                                {(getProfile(selectedUser)?.resume || getProfile(selectedUser)?.resumeUrl) && (
                                  <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 1 }}>Resume</Typography>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <PictureAsPdf sx={{ fontSize: 40, color: 'error.main' }} />
                                        <Box sx={{ flex: 1 }}>
                                          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                            {getProfile(selectedUser)?.name || 'Tutor'}'s Resume
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            Professional qualifications and experience
                                          </Typography>
                                        </Box>
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          startIcon={<OpenInNew />}
                                          href={getProfile(selectedUser)?.resume || getProfile(selectedUser)?.resumeUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          sx={{ borderRadius: 2, fontWeight: 700 }}
                                        >
                                          View Resume
                                        </Button>
                                      </Box>
                                    </Paper>
                                  </Box>
                                )}

                                {/* Demo Video / Intro Video */}
                                {(() => {
                                  const videoUrl = (getProfile(selectedUser) as any)?.introVideoUrl ||
                                    getProfile(selectedUser)?.demoVideo ||
                                    getProfile(selectedUser)?.demoVideoUrl;
                                  return videoUrl ? (
                                    <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 1 }}>Intro Video</Typography>
                                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                          <VideoFile sx={{ fontSize: 40, color: 'primary.main' }} />
                                          <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                              Introduction & Teaching Demo
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              Watch {getProfile(selectedUser)?.name || 'tutor'}'s teaching demonstration
                                            </Typography>
                                          </Box>
                                          <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<PlayCircle />}
                                            href={videoUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ borderRadius: 2, fontWeight: 700 }}
                                          >
                                            Watch Video
                                          </Button>
                                        </Box>
                                        <Box
                                          sx={{
                                            position: 'relative',
                                            width: '100%',
                                            paddingTop: '56.25%', // 16:9 aspect ratio
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            bgcolor: 'black',
                                            mt: 1
                                          }}
                                        >
                                          <video
                                            controls
                                            style={{
                                              position: 'absolute',
                                              top: 0,
                                              left: 0,
                                              width: '100%',
                                              height: '100%',
                                              objectFit: 'contain'
                                            }}
                                            src={videoUrl}
                                          >
                                            Your browser does not support the video tag.
                                          </video>
                                        </Box>
                                      </Paper>
                                    </Box>
                                  ) : null;
                                })()}

                                {/* Schedule Display for Tutors */}
                                {(getProfile(selectedUser)?.weeklyAvailability?.length > 0 || getProfile(selectedUser)?.timingSlots?.length > 0) && (
                                  <>
                                    <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 1 }}>Weekly Availability</Typography>
                                      <Paper variant="outlined" sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}>
                                        {getProfile(selectedUser)?.weeklyAvailability?.length > 0 ? (
                                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            {getProfile(selectedUser)?.weeklyAvailability.map((day: string, idx: number) => (
                                              <Chip key={idx} label={day} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
                                            ))}
                                          </Box>
                                        ) : (
                                          <Typography variant="body2" color="text.disabled">No availability set</Typography>
                                        )}
                                      </Paper>
                                    </Box>
                                    <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 1 }}>Timing Slots</Typography>
                                      <Paper variant="outlined" sx={{ p: 2, bgcolor: alpha(theme.palette.secondary.main, 0.05), borderRadius: 2, maxHeight: 300, overflow: 'auto' }}>
                                        {getProfile(selectedUser)?.timingSlots?.length > 0 ? (
                                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            {getProfile(selectedUser)?.timingSlots.map((slot: string, idx: number) => (
                                              <Chip key={idx} label={slot} size="small" color="secondary" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.75rem' }} />
                                            ))}
                                          </Box>
                                        ) : (
                                          <Typography variant="body2" color="text.disabled">No timing slots set</Typography>
                                        )}
                                      </Paper>
                                    </Box>
                                  </>
                                )}
                              </>
                            )}
                            <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 0.5 }}>Residential Address</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{getProfile(selectedUser)?.address || 'Address hidden or not provided'}</Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ py: 4, textAlign: 'center', color: 'text.disabled' }}>Profile synchronization in progress...</Typography>
                    )}
                  </Box>
                )}

                {detailsTab === 2 && (
                  <Box sx={{ p: 4 }}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        bgcolor: theme.palette.mode === 'light' ? 'grey.50' : alpha(theme.palette.common.white, 0.02),
                        borderRadius: 2,
                        maxHeight: 400,
                        overflow: 'auto',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem'
                      }}
                    >
                      <pre style={{ margin: 0 }}>{JSON.stringify(selectedUser, null, 2)}</pre>
                    </Paper>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
                      Audit log of raw Firestore data for support reference.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 4, py: 3, borderTop: 1, borderColor: 'divider', gap: 2 }}>
            {isEditMode ? (
              <>
                <Button
                  onClick={handleCancelEdit}
                  variant="outlined"
                  startIcon={<Close />}
                  disabled={saving}
                  sx={{ py: 1.5, borderRadius: 3, fontWeight: 700, flex: 1 }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveProfile}
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={16} /> : <Save />}
                  disabled={saving}
                  sx={{ py: 1.5, borderRadius: 3, fontWeight: 700, flex: 1 }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </>
            ) : (
              <Button onClick={() => setDetailsOpen(false)} variant="contained" fullWidth sx={{ py: 1.5, borderRadius: 3, fontWeight: 700 }}>
                Close Record
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Wallet Edit Dialog */}
        <Dialog
          open={walletEditOpen}
          onClose={handleCloseWalletEdit}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              boxShadow: theme.shadows[10],
            }
          }}
        >
          <DialogTitle sx={{ fontWeight: 800, pb: 2 }}>
            Edit Wallet Balance
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'grid', gap: 3, pt: 1 }}>
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                  Current Balance: <Typography component="span" sx={{ color: 'primary.main', fontWeight: 800 }}>₹{selectedUser?.wallet?.balance.toLocaleString() || '0'}</Typography>
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 1 }}>
                  Operation Type
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant={walletOperation === 'add' ? 'contained' : 'outlined'}
                    onClick={() => setWalletOperation('add')}
                    startIcon={<Add />}
                    color="success"
                    sx={{ flex: 1, borderRadius: 2, fontWeight: 700 }}
                  >
                    Add Money
                  </Button>
                  <Button
                    variant={walletOperation === 'subtract' ? 'contained' : 'outlined'}
                    onClick={() => setWalletOperation('subtract')}
                    startIcon={<Remove />}
                    color="error"
                    sx={{ flex: 1, borderRadius: 2, fontWeight: 700 }}
                  >
                    Deduct Money
                  </Button>
                  <Button
                    variant={walletOperation === 'set' ? 'contained' : 'outlined'}
                    onClick={() => setWalletOperation('set')}
                    startIcon={<Edit />}
                    color="primary"
                    sx={{ flex: 1, borderRadius: 2, fontWeight: 700 }}
                  >
                    Set Amount
                  </Button>
                </Box>
              </Box>

              <TextField
                fullWidth
                label="Amount (₹)"
                type="number"
                value={walletAmount}
                onChange={(e) => setWalletAmount(e.target.value)}
                variant="outlined"
                placeholder="Enter amount"
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>₹</Typography>,
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />

              <TextField
                fullWidth
                label="Description (Optional)"
                value={walletDescription}
                onChange={(e) => setWalletDescription(e.target.value)}
                variant="outlined"
                placeholder="e.g., Refund, Adjustment, Bonus"
                multiline
                rows={2}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />

              {walletAmount && !isNaN(parseFloat(walletAmount)) && parseFloat(walletAmount) > 0 && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: alpha(
                      walletOperation === 'add' ? theme.palette.success.main :
                        walletOperation === 'subtract' ? theme.palette.error.main :
                          theme.palette.primary.main,
                      0.1
                    ),
                    borderRadius: 2,
                    borderColor: alpha(
                      walletOperation === 'add' ? theme.palette.success.main :
                        walletOperation === 'subtract' ? theme.palette.error.main :
                          theme.palette.primary.main,
                      0.3
                    )
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 800, fontSize: '0.625rem', letterSpacing: 1, display: 'block', mb: 0.5 }}>
                    New Balance
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 900,
                      color: walletOperation === 'add' ? 'success.main' :
                        walletOperation === 'subtract' ? 'error.main' :
                          'primary.main'
                    }}
                  >
                    ₹{(() => {
                      const amount = parseFloat(walletAmount);
                      const current = selectedUser?.wallet?.balance || 0;
                      if (walletOperation === 'add') return (current + amount).toLocaleString();
                      if (walletOperation === 'subtract') return Math.max(0, current - amount).toLocaleString();
                      return amount.toLocaleString();
                    })()}
                  </Typography>
                </Paper>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 4, py: 3, borderTop: 1, borderColor: 'divider', gap: 2 }}>
            <Button
              onClick={handleCloseWalletEdit}
              variant="outlined"
              disabled={updatingWallet}
              sx={{ borderRadius: 3, fontWeight: 700, flex: 1 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateWallet}
              variant="contained"
              disabled={updatingWallet || !walletAmount || isNaN(parseFloat(walletAmount)) || parseFloat(walletAmount) <= 0}
              startIcon={updatingWallet ? <CircularProgress size={16} /> : <Save />}
              color={walletOperation === 'add' ? 'success' : walletOperation === 'subtract' ? 'error' : 'primary'}
              sx={{ borderRadius: 3, fontWeight: 700, flex: 1 }}
            >
              {updatingWallet ? 'Updating...' : `${walletOperation === 'add' ? 'Add' : walletOperation === 'subtract' ? 'Deduct' : 'Set'} Money`}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default Users;
